import styled, { css, type Interpolation, type RuleSet } from 'styled-components';
import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { pinkPeople } from '../../../../../styles/colors';

type Color = Interpolation<object>;

const addPromotionBackgroundColor = (promotionName: string, color: Color | RuleSet<object>) => css`
  &.marker_promotion_${promotionName} {
    .openx-ui-airport-marker-selected &,
    .openx-ui-marker-selected & {
      background-color: ${color};

      &:after,
      &:before {
        border-color: ${color} transparent transparent transparent;
      }
    }

    &:hover {
      background-color: ${color};

      &:after {
        border-color: ${color} transparent transparent transparent;
      }
    }
  }
`;

const addPromotionColors = (promotionName: string, color: Color | RuleSet<object>, lastMinuteDeals?: boolean) => css`
  &.marker_promotion_${promotionName}.has-icon {
    .openx-ui-airport-marker-selected &,
    .openx-ui-marker-selected & {
      background-color: ${
        lastMinuteDeals ? ({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary : color
      };
      border-color: ${lastMinuteDeals ? ({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault : color}
        transparent transparent transparent;
      color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};

      &:after,
      &:before {
        border-color: ${
            lastMinuteDeals ? ({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault : color
          }
          transparent transparent transparent;
      }
    }

    &:hover {
      background-color: ${
        lastMinuteDeals ? ({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary : color
      };

      &:after {
        border-color: ${
            lastMinuteDeals ? ({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault : color
          }
          transparent transparent transparent;
      }
    }
  }
`;

const addLastMinuteDealsStyles = css`
  &.marker_promotion_flash_sales.has-icon {
    .openx-ui-marker-selected & {
      .marker-content {
        color: white;
        background: transparent;
        background-clip: unset;
      }
    }
  }
`;

export const MarkerContainer = styled.div<{
  $iconBg?: Color;
  $hasIcon?: boolean;
  lastMinuteDeals: boolean;
  ref: React.MutableRefObject<HTMLDivElement | undefined>;
  promotionType?: PromotionType;
}>`
  min-width: 40px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  border-radius: 8px;
  text-align: center;
  ${({ theme }) => theme.kallisto.font.size['175']};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  background: #fff;
  cursor: pointer;
  justify-content: center;
  padding: 0 8px;
  height: 28px;
  transition: box-shadow 0.2s ease-in-out;
  filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.06))
    drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.04));

  picture {
    height: 14px;
  }

  ${({ promotionType, lastMinuteDeals }) => {
    if (promotionType === PROMOTION_TYPES.FLASH_SALES) {
      return css`
        ${lastMinuteDeals && addLastMinuteDealsStyles};
        ${addPromotionColors(
          'flash_sales',
          ({ theme }) => theme.kallisto.color.common.special.flashSales[500],
          lastMinuteDeals
        )};
      `;
    }

    if (promotionType === PROMOTION_TYPES.MY_PRICE) {
      return addPromotionColors('my_price', ({ theme }) => theme.kallisto.color.common.special.myPrice[700]);
    }

    if (promotionType === PROMOTION_TYPES.TO_LASTMINUTE) {
      return addPromotionBackgroundColor('to_lastminute', pinkPeople);
    }

    if (promotionType === PROMOTION_TYPES.LOYALTY) {
      return addPromotionBackgroundColor('loyalty', pinkPeople);
    }

    return css``;
  }}

  &[class*='marker_promotion_'] {
    padding-left: 0;
    transition:
      background-color 0.3s ease-out,
      color 0.3s ease-out;

    &.has-icon {
      padding: 0 8px 0 0;
    }

    &:after {
      transition: border-color 0.3s ease-out;
    }

    &:hover {
      .marker-content {
        color: white;

        .marker-currency {
          color: white;
        }
      }
    }

    .marker-content {
      padding-left: 8px;
    }
  }

  .marker-content {
    line-height: 20px;
  }

  .waviy {
    position: relative;
  }

  .waviy span {
    position: relative;
    display: inline-block;
    color: #000;
    text-transform: uppercase;
    animation: waviy 0.4s infinite alternate;
  }

  .waviy > span:nth-child(2) {
    animation-delay: 0.133333333s;
  }

  .waviy > span:nth-child(3) {
    animation-delay: 0.266666666s;
  }

  &:hover {
    box-shadow: rgb(0 0 0 / 30%) 0 0 6px 0;
  }

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-style: solid;
    border-width: 6px 6px 0;
    border-color: #fff transparent transparent transparent;
  }

  .openx-ui-marker-visited & {
    padding: 3px 8px;
  }

  .marker-content {
    font-size: 14px;
  }
`;
